import React from "react";
import { motion } from "framer-motion";
import { FileText, Linkedin, Users, Bot, Briefcase, LifeBuoy } from "lucide-react";
import { Reveal, Eyebrow, StaggerGroup, StaggerItem } from "./Reveal";
import { COURSE } from "../../data/course";

const ICONS = [FileText, Linkedin, Users, Bot, Briefcase, LifeBuoy];

export default function Placement() {
  const { placement } = COURSE;
  return (
    <section id="placement" data-testid="placement-section" className="relative py-24 md:py-32">
      <div className="pointer-events-none absolute left-1/4 top-0 h-[380px] w-[380px] glow-red opacity-40" />
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="max-w-2xl">
          <Reveal>
            <Eyebrow>Career Success &amp; Placement Ecosystem</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-[#293891] sm:text-5xl">
              We don&apos;t stop at teaching. <span className="text-[#3B82F6]">We get you hired.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-[#566076]">{placement.intro}</p>
          </Reveal>
        </div>

        <StaggerGroup className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {placement.items.map((item, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <StaggerItem
                key={item.title}
                className="card-glow group relative overflow-hidden rounded-3xl border border-[#293891]/10 bg-white p-8 shadow-sm"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#e63329]/10 text-[#e63329] transition-transform duration-500 group-hover:scale-110">
                  <Icon size={22} />
                </div>
                <h3 className="mt-6 font-display text-lg font-medium text-[#293891]">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#566076]">{item.desc}</p>
              </StaggerItem>
            );
          })}
        </StaggerGroup>

        {/* How placement works — step timeline */}
        <Reveal delay={0.1}>
          <div className="mt-16" data-testid="placement-timeline">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#566076]">How placement works</span>
            <div className="relative mt-8">
              <div className="pointer-events-none absolute left-0 right-0 top-5 hidden h-px bg-gradient-to-r from-[#3B82F6]/30 via-[#293891]/15 to-[#e63329]/40 lg:block" />
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-4">
                {placement.steps.map((step, i) => (
                  <motion.div
                    key={step.label}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                    className="relative flex flex-col items-start lg:items-center lg:text-center"
                  >
                    <span
                      className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full font-display text-sm font-semibold text-white shadow-md ${
                        i === placement.steps.length - 1 ? "bg-[#e63329]" : "bg-[#1A2A7A]"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <h4 className="mt-4 font-display text-base font-medium leading-snug text-[#293891]">{step.label}</h4>
                    <p className="mt-1 text-xs text-[#566076]">{step.sub}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
